import { authExchange } from '@urql/exchange-auth';
import { retryExchange } from '@urql/exchange-retry';
import {
  Client,
  type CombinedError,
  type Exchange,
  fetchExchange,
  mapExchange,
  provideClient,
} from '@urql/vue';
import { type DocumentNode, Kind, type OperationDefinitionNode } from 'graphql';
import { liffConfigManager } from '~/features/liffConfig/manager';

const retryExchangeGenerator = retryExchange({
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  randomDelay: true,
  maxNumberAttempts: 2,
  retryIf: (err: CombinedError) => !!(err && err.networkError !== undefined),
});

const getOperationName = (query: DocumentNode): string | null => {
  const operation = query.definitions.find(
    (node): node is OperationDefinitionNode =>
      node.kind === Kind.OPERATION_DEFINITION && !!node.name
  );

  if (operation) {
    return operation.name?.value || null;
  }

  return null;
};

export default defineNuxtPlugin(
  ({
    $config: {
      public: { graphqlEndpoint },
    },
    vueApp,
  }) => {
    const { $newrelic } = useNuxtApp();

    /**
     * GraphQLエラーをNew Relicに記録するExchange
     */
    const queryLogExchangeGenerator = (): Exchange =>
      mapExchange({
        onError(error, operation) {
          if (!$newrelic) return;

          $newrelic.noticeError(error, {
            gqlOperationName: getOperationName(operation.query) || 'unknown',
            gqlVariables: JSON.stringify(operation.variables),
          });
        },
      });

    const authExchangeGenerator = (): Exchange =>
      authExchange(async (utils) => {
        // 参考
        // https://commerce.nearform.com/open-source/urql/docs/advanced/authentication/

        return {
          addAuthToOperation: (operation) => {
            const liffConfigValue = liffConfigManager.getValue();
            if (liffConfigValue == null) {
              return operation;
            }

            return utils.appendHeaders(operation, {
              'X-LINE-ID-TOKEN': liffConfigValue.idToken,
              'X-LINE-CLIENT-ID': liffConfigValue.clientId,
              'X-LINE-ACTIVATION-PROVIDER-ID':
                liffConfigValue.lineActivationProviderId,
            });
          },
          didAuthError() {
            // エラーレスポンスを受け取ったとき、それが認証エラーかどうかを判定する
            // 必要なら実装する
            return false;
          },
          willAuthError() {
            // リクエストを送信する前に、認証エラーが発生するかどうかを判定する
            // 必要なら実装する
            return false;
          },
          async refreshAuth() {
            // 認証エラー時のリフレッシュ処理を行う
            // 必要なら実装する
          },
        };
      });

    const client = ref(
      new Client({
        url: graphqlEndpoint,
        exchanges: [
          retryExchangeGenerator,
          authExchangeGenerator(),
          queryLogExchangeGenerator(),
          fetchExchange,
        ],
        fetch,
        requestPolicy: 'network-only',
      })
    );

    vueApp.provide('$urql', client);
    provideClient(client);

    return {
      provide: {
        urql: client,
      },
    };
  }
);
